import { useState } from 'react';

import { Controller } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import useAppSelector from 'hooks/common/useAppSelector';
import { useInitPageFilters } from 'hooks/moderator/useInitFilters';

import AdminTableListPage from '../../../components/admin/AdminTableListPage';
import { InputV2 } from '../../../components/common/ui-kit/Input/view';
import ReactSelect from '../../../components/common/ui-kit/Select';
import { useGetExternalsPageableQuery } from '../../../redux/api/staff/admin/uploading-data-api';
import { createInputV2Props } from '../../../utils/form-helpers';

export enum ExternalEntityType {
  MOODLE_COURSE = 'Курсы MOODLE',
  ADMISSION_EDUCATION_LEVEL = 'Уровни образования',
  CAMPAIGN = 'Приёмная компания',
  PRIVILEGE_TYPE = 'Типы привилегий',
  PRIVILEGE_TYPE_MAPPING = 'PRIVILEGE_TYPE_MAPPING',
  OLYMPIAD_TYPE = 'Типы олимпиад',
  ACHIEVEMENT_TYPE = 'Типы достижений',
  TRAJECTORIES = 'Траектории',
  TARGET_ORGANIZATION = 'Целевые организации',
  EVALUATION_SYSTEM = 'Формы оценивания',
  CONFIRMATION_DOCUMENT_TYPE = 'Типы подтверждающих документов',
  SUBJECT = 'Предметы',
  CONTRACT_PAYMENT_TYPE = 'Формы оплаты договоров',
  CONTRACT_SPECIFIC = 'Типы договоров',
  ENTRANCE_TEST_TYPE_ENTITY = 'Виды вступительных испытаний',
  FACULTY = 'Факультеты',
  DIRECTION_PAYMENT_FORM = 'Формы оплаты',
  EDUCATION_PROGRAM = 'Образовательные программы',
  DIRECTION_EDUCATION_FORM = 'Формы образования',
  DIRECTION_EDUCATION_LEVEL = 'Уровни образования по направлениям',
  DIRECTION = 'Направления поступления',
  ENTRANCE_TESTS_SET = 'Наборы вступительных испытаний',
}

export const mapExternalEntityTypeToString = new Map<
  ExternalEntityType,
  string
>([
  [ExternalEntityType.MOODLE_COURSE, 'MOODLE_COURSE'],
  [ExternalEntityType.ADMISSION_EDUCATION_LEVEL, 'ADMISSION_EDUCATION_LEVEL'],
  [ExternalEntityType.CAMPAIGN, 'CAMPAIGN'],
  [ExternalEntityType.PRIVILEGE_TYPE, 'PRIVILEGE_TYPE'],
  [ExternalEntityType.PRIVILEGE_TYPE_MAPPING, 'PRIVILEGE_TYPE_MAPPING'],
  [ExternalEntityType.OLYMPIAD_TYPE, 'OLYMPIAD_TYPE'],
  [ExternalEntityType.ACHIEVEMENT_TYPE, 'ACHIEVEMENT_TYPE'],
  [ExternalEntityType.TRAJECTORIES, 'TRAJECTORIES'],
  [ExternalEntityType.TARGET_ORGANIZATION, 'TARGET_ORGANIZATION'],
  [ExternalEntityType.EVALUATION_SYSTEM, 'EVALUATION_SYSTEM'],
  [ExternalEntityType.CONFIRMATION_DOCUMENT_TYPE, 'CONFIRMATION_DOCUMENT_TYPE'],
  [ExternalEntityType.SUBJECT, 'SUBJECT'],
  [ExternalEntityType.CONTRACT_PAYMENT_TYPE, 'CONTRACT_PAYMENT_TYPE'],
  [ExternalEntityType.CONTRACT_SPECIFIC, 'CONTRACT_SPECIFIC'],
  [ExternalEntityType.ENTRANCE_TEST_TYPE_ENTITY, 'ENTRANCE_TEST_TYPE_ENTITY'],
  [ExternalEntityType.FACULTY, 'FACULTY'],
  [ExternalEntityType.DIRECTION_PAYMENT_FORM, 'DIRECTION_PAYMENT_FORM'],
  [ExternalEntityType.EDUCATION_PROGRAM, 'EDUCATION_PROGRAM'],
  [ExternalEntityType.DIRECTION_EDUCATION_FORM, 'DIRECTION_EDUCATION_FORM'],
  [ExternalEntityType.DIRECTION_EDUCATION_LEVEL, 'DIRECTION_EDUCATION_LEVEL'],
  [ExternalEntityType.DIRECTION, 'DIRECTION'],
  [ExternalEntityType.ENTRANCE_TESTS_SET, 'ENTRANCE_TESTS_SET'],
]);

export const renderExternalTablesFilter = ({
  control,
  formState: { errors },
  setType,
  visibleFilters,
}) => {
  mapExternalEntityTypeToString.get(ExternalEntityType.MOODLE_COURSE);
  const inputV2Props = createInputV2Props(errors);
  return (
    <>
      <>
        {visibleFilters.includes('type') && (
          <Controller
            name="type"
            control={control}
            render={({ field }) => (
              <ReactSelect
                options={Object.entries(ExternalEntityType).map(value => ({
                  value: value[0],
                  label: value[1],
                }))}
                placeholder="Тип выгрузки"
                className="filter-form__field"
                {...field}
                onChange={selectedOption => {
                  field.onChange(selectedOption);
                  setType(selectedOption.value);
                }}
              />
            )}
          />
        )}
        {visibleFilters.includes('id') && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <InputV2
                placeholder="ID"
                className="filter-form__field"
                {...inputV2Props(field)}
              />
            )}
          />
        )}
        {visibleFilters.includes('name') && (
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <InputV2
                placeholder="Название"
                className="filter-form__field"
                {...inputV2Props(field)}
              />
            )}
          />
        )}
        {visibleFilters.includes('deletedAt') && (
          <Controller
            name="deletedAt"
            control={control}
            render={({ field }) => (
              <ReactSelect
                options={[
                  { label: 'Да', value: 'true' },
                  { label: 'Нет', value: 'false' },
                ]}
                placeholder="Удален"
                className="filter-form__field"
                {...field}
              />
            )}
          />
        )}
        {visibleFilters.includes('externalId') && (
          <Controller
            name="externalId"
            control={control}
            render={({ field }) => (
              <InputV2
                placeholder="Внешний ID"
                className="filter-form__field"
                {...inputV2Props(field)}
              />
            )}
          />
        )}
      </>
    </>
  );
};

const externalTablesColumns = [
  { Header: 'ID', accessor: 'id' },
  { Header: 'Название', accessor: 'name' },
  { Header: 'Удален', accessor: 'deletedAt' },
  { Header: 'Внешний ID', accessor: 'externalId' },
];

const ExternalTablesPage = () => {
  const { pathname } = useLocation();
  const [type, setType] = useState(
    mapExternalEntityTypeToString.get(ExternalEntityType.MOODLE_COURSE)
  );
  const onRowClick = item => {
    window.open(`${pathname}/${item.id?.toString()}?type=${type}`, '_blank');
  };
  const visibleFilters =
    useAppSelector(state => state.visibleFilters.externals) ?? [];
  useInitPageFilters('externals');

  return (
    <AdminTableListPage
      title="Константы"
      storeKey="externals"
      query={{
        use: useGetExternalsPageableQuery,
      }}
      columns={externalTablesColumns}
      filtersConfig={{
        defaultValues: {
          type: {
            value: mapExternalEntityTypeToString.get(
              ExternalEntityType.MOODLE_COURSE
            ),
            label: ExternalEntityType.MOODLE_COURSE,
          },
        },
        renderFilters: props =>
          renderExternalTablesFilter({ ...props, setType, visibleFilters }),
      }}
      onRowClick={onRowClick}
      filterSettingsPageKey="externals"
    />
  );
};

export default ExternalTablesPage;
